import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { Person } from "../api/types";
import {
  emptyPersonEditor,
  editorFromPerson,
  PersonEditor,
  type PersonEditorState,
} from "../components/PersonEditor";

export function PeoplePage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editor, setEditor] = useState<PersonEditorState | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setPeople(await api<Person[]>("/api/people"));
    setError("");
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  const list = useMemo(
    () => people.filter((p) => showInactive || p.is_active),
    [people, showInactive],
  );

  async function save() {
    if (!editor) return;
    setBusy(true);
    setError("");
    try {
      if (!editor.display_name.trim() || !editor.email.trim()) {
        throw new Error("Name and email are required");
      }
      if (editor.mode === "add") {
        await api("/api/people", {
          method: "POST",
          body: JSON.stringify({
            display_name: editor.display_name.trim(),
            email: editor.email.trim().toLowerCase(),
            phone: editor.phone.trim() || null,
            role: editor.role,
            password: editor.password || null,
          }),
        });
        setNotice(`Added ${editor.display_name.trim()}`);
      } else {
        await api(`/api/people/${editor.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            display_name: editor.display_name.trim(),
            email: editor.email.trim().toLowerCase(),
            phone: editor.phone.trim() || null,
            role: editor.role,
            is_active: editor.is_active,
          }),
        });
        if (editor.password) {
          if (editor.password.length < 8) throw new Error("Password must be at least 8 characters");
          await api(`/api/people/${editor.id}/set-password`, {
            method: "POST",
            body: JSON.stringify({ password: editor.password }),
          });
        }
        setNotice(`Saved ${editor.display_name.trim()}`);
      }
      setEditor(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removePerson(id: number, name: string) {
    if (!window.confirm(`Remove ${name}? If they have passport history they will be deactivated.`)) return;
    const res = await api<{ mode?: string }>(`/api/people/${id}`, { method: "DELETE" });
    setNotice(res.mode === "deactivated" ? `${name} deactivated` : `${name} deleted`);
    setEditor(null);
    await load();
  }

  async function syncCal() {
    setBusy(true);
    try {
      const result = await api<{ created: number; updated: number }>("/api/integrations/cal/sync-people", {
        method: "POST",
      });
      setNotice(`CAL adapter synced — created ${result.created}, updated ${result.updated}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <div>
          <strong>People</strong>
          <span className="muted"> · Passport directory · vendor sync via integrations API</span>
        </div>
        <div className="toolbar-actions">
          <button type="button" className="tool-btn" onClick={() => setEditor(emptyPersonEditor())}>
            Add person
          </button>
          <button type="button" className="tool-btn" disabled={busy} onClick={() => void syncCal()}>
            Sync from CAL
          </button>
          <button type="button" className="tool-btn" onClick={() => load().catch((e) => setError(e.message))}>
            Refresh
          </button>
        </div>
      </div>

      <label className="people-check">
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Show inactive
      </label>

      {notice && <div className="people-notice">{notice}</div>}
      {error && <div className="banner-error">{error}</div>}

      {editor && (
        <PersonEditor
          editor={editor}
          busy={busy}
          onChange={setEditor}
          onSave={() => void save()}
          onCancel={() => setEditor(null)}
          onDelete={
            editor.id != null
              ? () => void removePerson(editor.id!, editor.display_name)
              : undefined
          }
        />
      )}

      <div className="people-table-wrap">
        <table className="people-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className={p.is_active ? undefined : "inactive"}>
                <td>{p.display_name}</td>
                <td>{p.email}</td>
                <td>{p.phone || "—"}</td>
                <td>{p.role}</td>
                <td>{p.is_active ? "active" : "off"}</td>
                <td className="people-actions">
                  <button type="button" onClick={() => setEditor(editorFromPerson(p))}>
                    Edit
                  </button>
                  <button type="button" className="danger" onClick={() => void removePerson(p.id, p.display_name)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr>
                <td colSpan={6}>No people yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
